import React, { useState, useMemo } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, Circle, Clock, AlertCircle, MessageSquare, Send } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const WorkflowSection = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { language } = useLanguage();
  const t = translations[language];

  const workflowSteps = useMemo(() => [
    {
      step: 0,
      title: t.workflow.step1.title,
      description: t.workflow.step1.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 }
      ],
      zendeskTickets: [],
      activeTicket: null,
    },
    {
      step: 1,
      title: t.workflow.step2.title,
      description: t.workflow.step2.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 }
      ],
      zendeskTickets: [],
      activeTicket: null,
    },
    {
      step: 2,
      title: t.workflow.step3.title,
      description: t.workflow.step3.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 }
      ],
      zendeskTickets: [
        { id: '#12345', subject: 'WeChat User Inquiry: Account Help', status: 'new', priority: 'normal', created: '10:23 AM', source: 'WeChat' }
      ],
      activeTicket: 0,
    },
    {
      step: 3,
      title: t.workflow.step4.title,
      description: t.workflow.step4.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 }
      ],
      zendeskTickets: [
        { id: '#12345', subject: 'WeChat User Inquiry: Account Help', status: 'open', priority: 'normal', created: '10:23 AM', source: 'WeChat', agent: 'Sarah Chen' }
      ],
      activeTicket: 0,
    },
    {
      step: 4,
      title: t.workflow.step5.title,
      description: t.workflow.step5.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 },
        { type: 'support', text: 'Hi! I\'m Sarah from Canva Support. I\'d be happy to help you with your account. Could you please provide more details about the issue you\'re experiencing?', time: '10:25 AM', id: 3 }
      ],
      zendeskTickets: [
        { id: '#12345', subject: 'WeChat User Inquiry: Account Help', status: 'open', priority: 'normal', created: '10:23 AM', source: 'WeChat', agent: 'Sarah Chen', lastUpdate: '10:25 AM' }
      ],
      activeTicket: 0,
    },
    {
      step: 5,
      title: t.workflow.step6.title,
      description: t.workflow.step6.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 },
        { type: 'support', text: 'Hi! I\'m Sarah from Canva Support. I\'d be happy to help you with your account. Could you please provide more details about the issue you\'re experiencing?', time: '10:25 AM', id: 3 },
        { type: 'user', text: 'I can\'t log in to my account. It says my password is incorrect.', time: '10:26 AM', id: 4 }
      ],
      zendeskTickets: [
        { id: '#12345', subject: 'WeChat User Inquiry: Account Help', status: 'open', priority: 'normal', created: '10:23 AM', source: 'WeChat', agent: 'Sarah Chen', lastUpdate: '10:26 AM' }
      ],
      activeTicket: 0,
    },
    {
      step: 6,
      title: t.workflow.step7.title,
      description: t.workflow.step7.description,
      wechatMessages: [
        { type: 'user', text: 'Hello, I need help with my Canva account', time: '10:23 AM', id: 1 },
        { type: 'system', text: 'Your message has been received. Processing...', time: '10:23 AM', id: 2 },
        { type: 'support', text: 'Hi! I\'m Sarah from Canva Support. I\'d be happy to help you with your account. Could you please provide more details about the issue you\'re experiencing?', time: '10:25 AM', id: 3 },
        { type: 'user', text: 'I can\'t log in to my account. It says my password is incorrect.', time: '10:26 AM', id: 4 },
        { type: 'support', text: 'I\'ve reset your password and sent a reset link to your email. Please check your inbox and follow the instructions to set a new password.', time: '10:28 AM', id: 5 },
        { type: 'user', text: 'Thank you so much! I received the email and reset my password. Everything works now!', time: '10:30 AM', id: 6 },
        { type: 'support', text: 'Great! I\'m glad I could help. If you need any further assistance, feel free to reach out anytime. Have a wonderful day!', time: '10:30 AM', id: 7 }
      ],
      zendeskTickets: [
        { id: '#12345', subject: 'WeChat User Inquiry: Account Help', status: 'solved', priority: 'normal', created: '10:23 AM', source: 'WeChat', agent: 'Sarah Chen', lastUpdate: '10:30 AM', resolved: '10:30 AM' }
      ],
      activeTicket: 0,
    },
  ], [t]);

  const currentWorkflow = workflowSteps[currentStep];

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { bg: 'bg-blue-100', text: 'text-blue-800', label: t.workflow.new },
      open: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t.workflow.open },
      pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: t.workflow.pending },
      solved: { bg: 'bg-green-100', text: 'text-green-800', label: t.workflow.solved },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: t.workflow.closed },
    };
    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap inline-block ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      urgent: { bg: 'bg-red-100', text: 'text-red-800', icon: <AlertCircle className="w-3 h-3" />, label: t.workflow.urgent },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', icon: <AlertCircle className="w-3 h-3" />, label: t.workflow.high },
      normal: { bg: 'bg-blue-100', text: 'text-blue-800', icon: null, label: t.workflow.normal },
      low: { bg: 'bg-gray-100', text: 'text-gray-800', icon: null, label: t.workflow.low },
    };
    const config = priorityConfig[priority] || priorityConfig.normal;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap inline-block ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <section
      id="workflow"
      className="min-h-screen py-24 bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.workflow.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] mx-auto rounded-full"></div>
          <p className="text-xl text-gray-600 mt-6 max-w-3xl mx-auto">
            {t.workflow.description}
          </p>
          <p className="text-sm text-gray-500 mt-4 italic max-w-3xl mx-auto">
            {t.workflow.note}
          </p>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center items-center mb-8 flex-wrap gap-2 md:gap-4">
          {workflowSteps.map((step, index) => (
            <React.Fragment key={index}>
              <button
                onClick={() => setCurrentStep(index)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 text-sm ${
                  currentStep === index
                    ? 'bg-[#00C4CC] text-white shadow-lg scale-110'
                    : currentStep > index
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {currentStep > index ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Circle className="w-4 h-4" />
                )}
                <span className="font-medium hidden sm:inline">{step.title}</span>
                <span className="font-medium sm:hidden">{index + 1}</span>
              </button>
              {index < workflowSteps.length - 1 && (
                <ArrowRight className="w-4 h-4 text-gray-400 hidden md:block" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Current Step Title */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {t.workflow.step} {currentStep + 1}: {currentWorkflow.title}
          </h3>
          <p className="text-gray-600">{currentWorkflow.description}</p>
        </div>

        {/* Demo Area - Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left Side - WeChat Interface Mockup */}
          <div className="relative bg-[#EDEDED] rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* WeChat Header */}
            <div className="bg-[#07C160] p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-[#07C160]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{t.workflow.wechatSupport}</h3>
                    <p className="text-xs text-white/80">{t.workflow.wechatOnline}</p>
                  </div>
                </div>
                <div className="text-xs">{t.workflow.wechat}</div>
              </div>
            </div>

            {/* WeChat Chat Area */}
            <div className="p-4 min-h-[500px] flex flex-col">
              <div className="flex-1 space-y-4 overflow-y-auto pb-80">
                {currentWorkflow.wechatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    {msg.type === 'support' && (
                      <div className="w-8 h-8 rounded-full bg-gray-300 mr-2 flex-shrink-0"></div>
                    )}
                    <div className="max-w-[75%]">
                        <p className="text-xs text-gray-500 mt-1 px-2">
                          {msg.time}
                        </p>
                      <div
                        className={`rounded-lg px-4 py-2 ${
                          msg.type === 'user'
                            ? 'bg-[#95EC69] text-gray-900'
                            : msg.type === 'support'
                            ? 'bg-white text-gray-900'
                            : 'bg-blue-100 text-blue-900'
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{msg.text}</p>
                      </div>

                    </div>
                    {msg.type === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-[#95EC69] ml-2 flex-shrink-0"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
              {/* WeChat Input Area */}
              <div className="absolute bottom-[15px] right-[2.5%] w-[95%] mt-4 flex items-center space-x-2 bg-white rounded-full px-4 py-3 border border-gray-200">
                <input
                  type="text"
                  placeholder={t.workflow.typeMessage}
                  className="flex-1 outline-none text-sm"
                  disabled
                />
                <button className="p-2 bg-[#07C160] text-white rounded-full hover:bg-[#06AD56] transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
          </div>

          {/* Right Side - Zendesk Interface Mockup */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* Zendesk Header */}
            <div className="bg-[#03363D] p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                    <span className="text-[#03363D] font-bold text-xs">Z</span>
                  </div>
                  <h3 className="font-semibold">{t.workflow.zendeskSupport}</h3>
                </div>
                <div className="text-xs text-white/80">{t.workflow.zendeskAdmin}</div>
              </div>
            </div>

            {/* Zendesk Content Area */}
            <div className="p-6 min-h-[500px] bg-gray-50">
              {/* Ticket List View */}
              {currentWorkflow.zendeskTickets.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t.workflow.ticketId}</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t.workflow.ticketSubject}</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t.workflow.ticketStatus}</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t.workflow.ticketPriority}</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t.workflow.ticketSource}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentWorkflow.zendeskTickets.map((ticket, idx) => (
                          <tr
                            key={idx}
                            className={`border-b hover:bg-blue-50 transition-colors ${
                              currentWorkflow.activeTicket === idx ? 'bg-blue-50' : ''
                            }`}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{ticket.id}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{ticket.subject}</td>
                            <td className="px-4 py-3">{getStatusBadge(ticket.status)}</td>
                            <td className="px-4 py-3">{getPriorityBadge(ticket.priority)}</td>
                            <td className="px-4 py-3">
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                {ticket.source}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Ticket Details Panel */}
                  {currentWorkflow.activeTicket !== null && currentWorkflow.zendeskTickets[currentWorkflow.activeTicket] && (
                    <div className="bg-white rounded-lg shadow-sm p-6 mt-4">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-1">
                            {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].subject}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {t.workflow.ticketDetails} {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].id}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].status)}
                          {getPriorityBadge(currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].priority)}
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            {t.workflow.created}: {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].created}
                          </span>
                        </div>
                        {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].agent && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">
                              {t.workflow.assignedTo}: <span className="font-semibold">{currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].agent}</span>
                            </span>
                          </div>
                        )}
                        {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].lastUpdate && (
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {t.workflow.lastUpdated}: {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].lastUpdate}
                            </span>
                          </div>
                        )}
                        {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].resolved && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-green-600 font-semibold">
                              {t.workflow.resolved}: {currentWorkflow.zendeskTickets[currentWorkflow.activeTicket].resolved}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Conversation History */}
                      <div className="mt-6 pt-6 border-t">
                        <h5 className="font-semibold text-gray-900 mb-3">{t.workflow.conversationHistory}</h5>
                        <div className="space-y-3">
                          {currentWorkflow.wechatMessages
                            .filter(msg => msg.type !== 'system')
                            .map((msg, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg ${
                                  msg.type === 'user'
                                    ? 'bg-blue-50 border-l-4 border-blue-500'
                                    : 'bg-gray-50 border-l-4 border-gray-400'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-semibold text-gray-700">
                                    {msg.type === 'user' ? t.workflow.userViaWeChat : t.workflow.supportAgent}
                                  </span>
                                  <span className="text-xs text-gray-500">{msg.time}</span>
                                </div>
                                <p className="text-sm text-gray-700">{msg.text}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">{t.workflow.noTickets}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-center items-center space-x-4">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t.workflow.previous}</span>
          </button>
          <button
            onClick={() => setCurrentStep(Math.min(workflowSteps.length - 1, currentStep + 1))}
            disabled={currentStep === workflowSteps.length - 1}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] text-white rounded-lg font-medium hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
          >
            <span>{t.workflow.nextStep}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
